/**
 * EditingConfig — where the client hands over the visuals an editing product is
 * produced from.
 *
 * This used to sit in the product drawer, before the line was even in the cart,
 * which put the one input the product is MADE from in the place meant for
 * choosing and pricing it. Editing had no configuration page to move it to — on
 * the dev catalogue thirteen upload-taking products announce no configuration at
 * all — so the page is this module, and the flow that routes to it is derived
 * FE-side (`utils/purchaseFlowRevamp/sourceUploadFlow.ts`).
 *
 * `FileUploadSection` is reused verbatim rather than reimplemented: it already
 * narrows the shared FileAPI scope to this product, filters out files another line
 * owns, and — critically — UNCLAIMS a filename before dropping its scope entry, so
 * a delete is not undone by `useResumedUploads` re-deriving the scope from the
 * attachments. `useEditingSourceSync` supplies the scope, makes the matching claim,
 * and re-derives how many produced items the line is billed for.
 *
 * The client's brief is collected here for the same reason, moved off the drawer
 * by ISSUE-3386's split: a note about "these photos" belongs where the photos are
 * handed over. `productCommentSurface` owns that decision so the two surfaces can
 * never both show a box for one `CartItem.note`.
 *
 * The AI sky settings are collected here too, for the products that announce
 * them: they apply TO these visuals, so the client picks a sky with the photos it
 * will be applied to in front of them. They are stored on the CART LINE
 * (`CartItem.aiEditing`), which is where `serializePlacement` already reads them
 * from — moving the surface did not need to move the storage.
 */

import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { SystemMessage } from 'components/common/SystemMessage'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { FileUploadSection } from '../../components/productDrawer/FileUploadSection.component'
import { ProductCommentSection } from '../../components/productDrawer/ProductCommentSection.component'
import { STAGING_SENTINEL_PRODUCT_ID } from '../../fileHub'
import { AiEditingConfig } from './AiEditingConfig.component'
import { useEditingSourceSync } from './useEditingSourceSync'
import { useLineOffersComment } from './useLineOffersComment'
import { PfRevampEventName } from '../../analytics/events'
import { dispatchPfRevampEvent } from '../../analytics/usePfRevampAnalytics'

const AI_EDITING_DEFAULT = { skyReplacement: false, cloudType: null } as const

export interface EditingConfigProps {
  cartItemId: CartItemId
}

/**
 * @component EditingConfig
 * Source-visual hand-over for an editing line.
 */
export const EditingConfig: React.FC<EditingConfigProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { scopeKey, variantId, claimedByOtherLines, claimedFileCount, isOnePerItem, collectsAiSettings } =
    useEditingSourceSync(cartItemId)

  const aiEditing = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.aiEditing) ?? AI_EDITING_DEFAULT
  const setItemAiEditing = usePfStore((s) => s.setItemAiEditing)

  const offersComment = useLineOffersComment(cartItemId)
  const note = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.note) ?? ''
  const setItemNote = usePfStore((s) => s.setItemNote)

  return (
    <Stack gap={1.2} data-cy="pf-revamp-editing-config">

      <Typography variant="text-lg" fontWeight={550} color={GRAY_900}>
        {t('purchase_flow_revamp:configuration.source_visuals_title')}
      </Typography>

      {/* One produced item per photo, so the number handed over is the number
          bought — said out loud, because the price follows it. */}
      {isOnePerItem &&
        <Typography variant="text-sm" color={GRAY_700}>
          {t('purchase_flow_revamp:configuration.source_visuals_one_per_item')}
        </Typography>
      }

      <FileUploadSection
        uploadProductId={STAGING_SENTINEL_PRODUCT_ID}
        productCode={variantId}
        scopeKey={scopeKey}
        claimedByOtherLines={claimedByOtherLines}
        ownerId={cartItemId}
      />

      {/* The step's Proceed gate refuses this line without a file; saying so here
          is what stops that refusal being a dead end the client cannot explain. */}
      {claimedFileCount === 0 &&
        <SystemMessage
          variant="warning"
          message={t('purchase_flow_revamp:configuration.source_visuals_required')}
        />
      }

      {collectsAiSettings &&
        <Stack gap={1.2} sx={{ mt: 1.2 }}>

          <Typography variant="text-lg" fontWeight={550} color={GRAY_900}>
            {t('purchase_flow_revamp:configuration.editing_settings_title')}
          </Typography>

          <AiEditingConfig
            value={aiEditing}
            onToggleSkyReplacement={(enabled) => {
              const next = enabled
                ? { ...aiEditing, skyReplacement: true }
                // The BE takes `cloudType` only as a qualifier of an enabled
                // replacement, so turning it off must not ship a stale one.
                : { skyReplacement: false, cloudType: null }
              setItemAiEditing(cartItemId, next)
              dispatchPfRevampEvent({
                type: PfRevampEventName.AI_EDITING_OPTION_SET,
                payload: { cartItemId, skyReplacement: next.skyReplacement, skyType: next.cloudType },
              })
            }}
            onSelectCloudType={(cloudType) => {
              setItemAiEditing(cartItemId, { ...aiEditing, cloudType })
              dispatchPfRevampEvent({
                type: PfRevampEventName.AI_EDITING_OPTION_SET,
                payload: { cartItemId, skyReplacement: aiEditing.skyReplacement, skyType: cloudType },
              })
            }}
          />

        </Stack>
      }

      {/* The client's own brief, here rather than in the drawer because it
          describes the visuals handed over above it. */}
      {offersComment &&
        <Stack sx={{ mt: 1.2 }}>
          <ProductCommentSection plain value={note} onChange={(next) => setItemNote(cartItemId, next)} />
        </Stack>
      }

    </Stack>
  )
}
